// React and related imports
import React from 'react';

// React Router
import { useParams, useNavigate } from 'react-router-dom';

// i18n hooks
import { useTranslation } from 'react-i18next';

// Hooks
import { usePageTitle } from '../hooks/usePageTitle';

// Chart components
import { QuestionsBarChart } from '../components/charts/QuestionsBarChart';
import { GenderPieChart } from '../components/charts/GenderPieChart';
import { AgeGroupsBarChart } from '../components/charts/AgeGroupsBarChart';

// RTK Query API hooks
import { useFetchOnboardingQuestionnaireQuestionsAnalyticsQuery } from '../redux/api/statisticsApi';

// Utilities
import { getLocalizedValueFromMultiLangValues } from '../utils/getLocalizedValueFromMultiLangValues';
import { Loading } from '../components/Shared/Loading/Loading';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import {
  selectIsDarkMode,
  selectIsRTL,
} from '../redux/selectors/displaySettingsSelectors';
import { useAppSelector } from '../redux/hooks/useAppSelector';
import { Button } from 'flowbite-react';

export const QuestionnaireAnalyticsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isRTL = useAppSelector(selectIsRTL);
  const isDarkMode = useAppSelector(selectIsDarkMode);

  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const buttonClassName = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;

  usePageTitle(t('pageTitles.questionnaireAnalytics'));

  const {
    data: questionsAnalytics,
    isLoading,
    error,
  } = useFetchOnboardingQuestionnaireQuestionsAnalyticsQuery({
    questionnaireId: questionnaireId || '',
  });

  const handleBackToDashboard = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (error || !questionsAnalytics?.data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <ErrorComponent errorMessage={t('common.error.loadingData')} />
      </div>
    );
  }

  const questionnaireName = getLocalizedValueFromMultiLangValues(
    questionsAnalytics.data.participationMetrics.byQuestionnaire[0]
      ?.questionnaireName || []
  );

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-black dark:text-primary-25">
            {t('questionnaireAnalytics.title')}
          </h1>
          <p className="mt-1 text-sm text-black dark:text-primary-25">
            {questionnaireName}
          </p>
        </div>
        <Button
          onClick={handleBackToDashboard}
          size="xs"
          className={`${buttonClassName}`}
        >
          {t('common.buttons.backToDashboard')}
        </Button>
      </div>

      {/* Analytics Summary */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg bg-white p-4 text-black shadow">
          <h3 className="text-sm font-medium text-black dark:text-primary-25">
            {t('questionnaireAnalytics.totalParticipants')}
          </h3>
          <p className="mt-1 text-2xl font-semibold text-black dark:text-primary-25">
            {questionsAnalytics.data.participationMetrics.totalParticipants}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 text-black shadow">
          <h3 className="text-sm font-medium text-black dark:text-primary-25">
            {t('questionnaireAnalytics.totalQuestions')}
          </h3>
          <p className="mt-1 text-2xl font-semibold text-black dark:text-primary-25">
            {questionsAnalytics.data.responseAnalytics.length}
          </p>
        </div>
        <div className="rounded-lg bg-white p-4 text-black shadow">
          <h3 className="text-sm font-medium text-black dark:text-primary-25">
            {t('questionnaireAnalytics.totalResponses')}
          </h3>
          <p className="mt-1 text-2xl font-semibold text-black dark:text-primary-25">
            {questionsAnalytics.data.responseAnalytics.reduce(
              (total, question) =>
                total +
                question.responses.reduce(
                  (sum, response) => sum + response.count,
                  0
                ),
              0
            )}
          </p>
        </div>
      </div>

      {/* Demographics Section */}
      {questionsAnalytics.data.demographics && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-black dark:text-primary-25">
            {t('questionnaireAnalytics.demographics')}
          </h2>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Gender Distribution */}
            {questionsAnalytics.data.demographics.gender && (
              <div className="rounded-2xl bg-white p-4 text-black shadow dark:text-primary-25">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold dark:text-primary-25">
                    {t('questionnaireAnalytics.genderDistribution')}
                  </h3>
                  <p className="mt-1 text-sm dark:text-primary-25">
                    {t('questionnaireAnalytics.genderDistributionDescription')}
                  </p>
                </div>
                <GenderPieChart
                  data={questionsAnalytics.data.demographics.gender}
                />
              </div>
            )}

            {/* Age Groups Distribution */}
            {questionsAnalytics.data.demographics.ageGroups && (
              <div className="rounded-2xl bg-white p-4 text-black shadow dark:text-primary-25">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold dark:text-primary-25">
                    {t('questionnaireAnalytics.ageGroupsDistribution')}
                  </h3>
                  <p className="mt-1 text-sm dark:text-primary-25">
                    {t(
                      'questionnaireAnalytics.ageGroupsDistributionDescription'
                    )}
                  </p>
                </div>
                <AgeGroupsBarChart
                  data={questionsAnalytics.data.demographics.ageGroups}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Questions Grid */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-black dark:text-primary-25">
          {t('questionnaireAnalytics.questionBreakdown')}
        </h2>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {[...questionsAnalytics.data.responseAnalytics]
            .sort((a, b) => a.questionId - b.questionId)
            .map((questionData) => {
              const chartData = [...questionData.responses]
                .sort((a, b) =>
                  isRTL ? b.answerId - a.answerId : a.answerId - b.answerId
                )
                .map((response) => ({
                  AnswerTitle: getLocalizedValueFromMultiLangValues(
                    response.answerText
                  ),
                  AnswerCount: response.count,
                }));

              return (
                <div
                  key={questionData.questionId}
                  className="rounded-2xl bg-white p-4 text-black shadow dark:text-primary-25"
                >
                  <div className="mb-4">
                    <h3 className="text-lg font-semibold dark:text-primary-25">
                      {t('questionnaireAnalytics.questionNumber', {
                        number: questionData.questionId + 1,
                      })}
                    </h3>
                    <p className="mt-1 text-sm dark:text-primary-25">
                      {getLocalizedValueFromMultiLangValues(
                        questionData.questionBody
                      )}
                    </p>
                  </div>
                  <QuestionsBarChart data={chartData} />
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};
